import { ToggleTree } from '@components'

import { DragEvent, ReactElement, useCallback, useMemo } from 'react'
import { useMatch, useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import { useDocumentsDragAndDrop } from '../../contexts/DocumentsDragAndDropContext'
import { useExpandedFolders } from '../../hooks/useExpandedFolders'
import { useFetchFolderTree } from '../../hooks/useFetchFolderTree'
import { useIsClientDocumentsRoute } from '../../hooks/useIsClientDocumentsRoute'
import { getDocumentsRoutes } from '../../utils/getDocumentsRoutes'
import { slugifyFolderName } from '../../utils/slugifyFolderName'
import { FolderTreeSkeleton } from './FolderTreeSkeleton'
import * as Styled from './styles'

export const FolderTree = (): ReactElement | null => {
  const { organization } = useUserOrganization()
  const navigate = useNavigate()
  const isClientDocumentsRoute = useIsClientDocumentsRoute()
  const documentsRoutes = getDocumentsRoutes(isClientDocumentsRoute)
  const match = useMatch(
    getOrganizationViewUrl(organization?.url, documentsRoutes.getFolderRoute(':folderId', ':slug?')),
  )
  const { tree, isLoading, isEmpty } = useFetchFolderTree()
  const { expandedIds, handleToggle } = useExpandedFolders()
  const { draggedItem, moveDraggedItemToFolder } = useDocumentsDragAndDrop()

  const folderId = match?.params.folderId

  const nodesById = useMemo(() => Object.fromEntries((tree ?? []).map((node) => [node.id, node])), [tree])

  const isDescendantFolder = useCallback(
    (candidateId: string, ancestorId: string): boolean => {
      let current = nodesById[candidateId]
      while (current?.parentId) {
        if (current.parentId === ancestorId) {
          return true
        }
        current = nodesById[current.parentId]
      }
      return false
    },
    [nodesById],
  )

  const canDropOnNode = useCallback(
    (id: string): boolean => {
      if (!draggedItem) {
        return false
      }
      if (draggedItem.id === id || draggedItem.sourceFolderId === id) {
        return false
      }
      if (draggedItem.type === 'folder' && isDescendantFolder(id, draggedItem.id)) {
        return false
      }
      return true
    },
    [draggedItem, isDescendantFolder],
  )

  const handleFolderSelect = useCallback(
    (id: string) => {
      if (!organization) {
        return
      }
      const node = tree?.find((n) => n.id === id)
      const slug = node ? slugifyFolderName(node.name) : undefined
      const route = documentsRoutes.getFolderRoute(id, slug)
      navigate(getOrganizationViewUrl(organization.url, route))
    },
    [documentsRoutes, navigate, organization, tree],
  )

  const handleNodeDragOver = useCallback(
    (id: string, event: DragEvent) => {
      if (canDropOnNode(id)) {
        event.preventDefault()
      }
    },
    [canDropOnNode],
  )

  const handleNodeDrop = useCallback(
    (id: string, event: DragEvent) => {
      if (canDropOnNode(id)) {
        event.preventDefault()
        moveDraggedItemToFolder(id)
      }
    },
    [canDropOnNode, moveDraggedItemToFolder],
  )

  if (isLoading) {
    return <FolderTreeSkeleton />
  }

  if (isEmpty || !tree) {
    return null
  }

  return (
    <Styled.FolderTreeWrapper>
      <ToggleTree
        tree={tree}
        selectedId={folderId}
        onNodeDragOver={handleNodeDragOver}
        onNodeDrop={handleNodeDrop}
        onSelect={handleFolderSelect}
        expandedIds={expandedIds}
        onToggle={handleToggle}
      />
    </Styled.FolderTreeWrapper>
  )
}
